import axios from 'axios';
import { useRouter } from 'vue-router';
import { authService } from './authService';

class SessionService {
  private sessionTimer: number | null = null;
  private activityTimer: number | null = null;
  private readonly sessionTimeout = 30 * 60 * 1000; // 30 Minuten
  private lastActivity: number = Date.now();
  private static instance: SessionService;

  constructor() {
    if (SessionService.instance) {
      return SessionService.instance;
    }
    SessionService.instance = this;
    this.setupActivityListeners();
  }

  private setupActivityListeners(): void {
    // Liste der Events, die als Benutzeraktivität zählen
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'keypress'
    ];

    // Event-Listener für jedes Event hinzufügen
    events.forEach((event) => {
      document.addEventListener(event, () => this.updateLastActivity());
    });

    // Auch AJAX-Anfragen als Aktivität zählen
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string) {
      this.addEventListener('load', () => {
        if (SessionService.instance) {
          SessionService.instance.updateLastActivity();
        }
      });
      return originalOpen.call(this, method, url, true);
    };
  }

  private updateLastActivity(): void {
    this.lastActivity = Date.now();

    // Aktivitätszeit auch im localStorage speichern für authService
    const activityTimeKey = 'last_activity_time';
    localStorage.setItem(activityTimeKey, this.lastActivity.toString());
  }

  startSessionTimer(): void {
    // Bestehenden Timer löschen
    if (this.sessionTimer) {
      this.stopSessionTimer();
    }

    // Timer für Aktivitätsprüfung alle 5 Minuten
    this.activityTimer = window.setInterval(
      () => {
        const inactiveTime = Date.now() - this.lastActivity;

        // Wenn der Benutzer länger als die Timeout-Zeit inaktiv war
        if (inactiveTime >= this.sessionTimeout) {
          this.handleSessionTimeout();
        }
      },
      5 * 60 * 1000
    ); // Prüfe alle 5 Minuten
  }

  stopSessionTimer(): void {
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
      this.sessionTimer = null;
    }
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
  }

  // Öffentliche Methode zur Sessionprüfung
  checkSession(): boolean {
    if (!authService.isAuthenticated()) {
      this.handleSessionTimeout();
      return false;
    }
    return true;
  }

  // Wirft einen Fehler, wenn die Session ungültig ist
  checkSessionAndThrow(): void {
    if (!authService.isAuthenticated()) {
      throw new Error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
    }
  }

  // Prüft die Session-Gültigkeit auf dem Server
  async checkSessionValidity(): Promise<boolean> {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;

    try {
      await axios.get('/api/v1/account/session', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return true;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }

  private async handleSessionTimeout(): Promise<void> {
    this.stopSessionTimer();
    await authService.logout();

    // Prüfen, ob bereits zur Login-Seite weitergeleitet wurde
    const redirectedToLogin = sessionStorage.getItem('redirected_to_login') === 'true';

    // Nur weiterleiten, wenn noch nicht weitergeleitet wurde
    if (!redirectedToLogin) {
      // Flag setzen, dass wir bereits weitergeleitet haben
      sessionStorage.setItem('redirected_to_login', 'true');

      const currentPath = window.location.pathname;
      const redirectPath = currentPath === '/login' ? '/' : currentPath;
      const router = useRouter();
      router.push({
        name: 'login-lang',
        query: { redirect: redirectPath },
        params: { lang: router.currentRoute.value.params.lang || 'de' }
      });
    }
  }

  // Methode zum Abrufen der verbleibenden Zeit
  getRemainingTime(): number {
    const inactiveTime = Date.now() - this.lastActivity;

    // Prüfen, ob die authService-Aktivitätszeit aktueller ist
    const authLastActivity = this.getAuthServiceLastActivity();
    const authInactiveTime = authLastActivity ? Date.now() - authLastActivity : Infinity;

    // Die kürzere Inaktivitätszeit verwenden (neueste Aktivität)
    const effectiveInactiveTime = Math.min(inactiveTime, authInactiveTime);

    return Math.max(0, this.sessionTimeout - effectiveInactiveTime);
  }

  // Hilfsmethode zum Abrufen der letzten Aktivitätszeit aus dem authService
  private getAuthServiceLastActivity(): number | null {
    const activityTimeKey = 'last_activity_time'; // Muss dem Key in authService entsprechen
    const timeStr = localStorage.getItem(activityTimeKey);
    return timeStr ? parseInt(timeStr) : null;
  }
}

// Singleton-Instanz exportieren
export const sessionService = new SessionService();
